export type ReactionKind = 'love' | 'like' | 'dislike' | 'laugh' | 'emphasize' | 'question';

export const REACTION_EMOJI: Record<ReactionKind, string> = {
  love: '❤️',
  like: '👍',
  dislike: '👎',
  laugh: '😂',
  emphasize: '‼️',
  question: '❓',
};

export type ReactionBadge = {
  emoji: string;
  label: string;
  actor?: string;
};

export const extractReactionSummary = (metadata?: Record<string, any> | null): ReactionBadge[] => {
  if (!metadata) return [];
  const raw = metadata.reactions ?? metadata.tapbacks ?? metadata.reaction_summary;
  if (!Array.isArray(raw)) return [];

  return raw
    .map((item) => {
      if (typeof item === 'string') {
        const key = item.toLowerCase() as ReactionKind;
        const emoji = (REACTION_EMOJI as any)[key] ?? '💬';
        return { emoji, label: item };
      }
      if (item && typeof item === 'object') {
        const typed = item as Record<string, any>;
        const typeValue =
          typeof typed.reaction === 'string'
            ? typed.reaction
            : typeof typed.type === 'string'
              ? typed.type
              : undefined;
        const key = (typeValue || '').toLowerCase() as ReactionKind;
        const emoji = (REACTION_EMOJI as any)[key] ?? '💬';
        const actor = [typed.actor, typed.from, typed.sender].find((v) => typeof v === 'string') as string | undefined;
        return { emoji, label: typeValue ?? 'reaction', actor };
      }
      return null;
    })
    .filter(Boolean) as ReactionBadge[];
};

type ReactionFoldMessage = {
  id: string;
  text: string;
  createdAt: Date;
  messageType?: string;
  metadata?: Record<string, any>;
};

const normalizeWhitespace = (value: string): string => value.trim().replace(/\s+/g, ' ');

const isReactionKind = (value: string): value is ReactionKind => {
  const lowered = value.toLowerCase() as ReactionKind;
  return Object.prototype.hasOwnProperty.call(REACTION_EMOJI, lowered);
};

/**
 * Fold message_type="reaction" messages into the referenced message as metadata.reactions,
 * and remove standalone reaction messages from the timeline (iMessage-style rendering).
 *
 * Note: Agent Service does not expose message_ref_id directly; we best-effort link via
 * metadata.original_message + temporal proximity (closest preceding matching message).
 */
export const foldReactionMessages = <T extends ReactionFoldMessage>(messages: T[]): T[] => {
  if (!Array.isArray(messages) || messages.length === 0) return messages;

  const baseMessages = messages.filter((m) => String(m.messageType || '').toLowerCase() !== 'reaction');
  const reactionEvents = messages.filter((m) => String(m.messageType || '').toLowerCase() === 'reaction');

  if (reactionEvents.length === 0) return baseMessages;

  const updatedById = new Map<string, T>();
  const getMessage = (id: string): T | undefined => {
    const updated = updatedById.get(id);
    if (updated) return updated;
    return baseMessages.find((m) => m.id === id);
  };

  const setMessage = (id: string, next: T) => {
    updatedById.set(id, next);
  };

  const getExistingReactions = (metadata: Record<string, any> | undefined): any[] => {
    if (!metadata) return [];
    const raw = metadata.reactions ?? metadata.tapbacks ?? metadata.reaction_summary;
    return Array.isArray(raw) ? raw : [];
  };

  const hasReactionKind = (existing: any[], reaction: ReactionKind): boolean => {
    const normalized = reaction.toLowerCase();
    for (const item of existing) {
      if (typeof item === 'string' && item.toLowerCase() === normalized) return true;
      if (item && typeof item === 'object') {
        const typed = item as Record<string, any>;
        const typeValue =
          typeof typed.reaction === 'string'
            ? typed.reaction
            : typeof typed.type === 'string'
              ? typed.type
              : undefined;
        if (typeof typeValue === 'string' && typeValue.toLowerCase() === normalized) return true;
      }
    }
    return false;
  };

  for (const reactionEvent of reactionEvents) {
    const rawKind = normalizeWhitespace(String(reactionEvent.text || '')).toLowerCase();
    if (!rawKind || !isReactionKind(rawKind)) continue;

    const originalRaw = reactionEvent.metadata?.original_message;
    if (typeof originalRaw !== 'string') continue;
    const original = normalizeWhitespace(originalRaw);
    if (!original) continue;

    const reactionCreatedAt = reactionEvent.createdAt?.getTime?.() || 0;
    if (!reactionCreatedAt) continue;

    // Best-effort: find closest preceding message with matching content.
    let targetId: string | null = null;
    for (let i = baseMessages.length - 1; i >= 0; i -= 1) {
      const candidate = getMessage(baseMessages[i].id) ?? baseMessages[i];
      const candidateCreatedAt = candidate.createdAt?.getTime?.() || 0;
      if (!candidateCreatedAt) continue;
      if (candidateCreatedAt > reactionCreatedAt + 1000) continue; // tolerate minor clock skew
      if (normalizeWhitespace(String(candidate.text || '')) !== original) continue;
      targetId = candidate.id;
      break;
    }
    if (!targetId) continue;

    const current = getMessage(targetId);
    if (!current) continue;

    const currentMetadata = current.metadata ?? {};
    const existing = getExistingReactions(currentMetadata);
    if (hasReactionKind(existing, rawKind)) continue;

    const nextMetadata = {
      ...currentMetadata,
      reactions: [...existing, rawKind],
    };

    setMessage(targetId, { ...current, metadata: nextMetadata } as T);
  }

  return baseMessages.map((m) => updatedById.get(m.id) ?? m);
};


